import {
    findPredefinedThicknessIndex,
    predefinedPanelThicknesses,
    zFightingFixDistance,
} from "@dextall/panels-defaults";
import { editorCladdingGeometryThickness } from "../defaults";
import { FacadeElementType } from "./facadeElementType";
import type { ICornerPanelSource } from "../../../responses/cornerPanelSource";
import type { IModelCorner } from "../../../responses/modelCorner";
import type { CladdingCellStyle } from "../../../responses/panelSource";
import type { IPanelType } from "../../../responses/panelType";
import type { BoundaryLoop } from "../boundaries/boundaryLoop";
import type { ExtrusionGeometry } from "../geometry/claddingCellExtrusionGeometryFactory";
import type { CardinalDirection } from "../geometry/compass";
import type { CornerCladdingsFactory } from "./cornerCladdingsFactory";
import type { Depth, InstallationSequence, PanelParameters } from "./panelParameters";
import type { WallCornerCladdingCell } from "./wallCornerCladdingCell";
import type { WallPanelCladdingCell } from "./wallPanelCladdingCell";

export class Corner {
    private readonly wingPlanesGeometry: ExtrusionGeometry[];
    private readonly _depth: Depth;
    private _elementName: string;
    private _installationSequence: InstallationSequence;

    constructor(
        private readonly panelSource: ICornerPanelSource,
        private readonly wallCorner: IModelCorner,
        public readonly panelDbId: number,
        public readonly leftWingHooksCoordinateSystem: THREE.Matrix4,
        public readonly rightWingHooksCoordinateSystem: THREE.Matrix4,
        modelOffset: THREE.Vector3,
        cornerCladdingsFactory: CornerCladdingsFactory,
        wingBoundaryPlaneMaterial: THREE.Material,
        private readonly leftWallBoundaryLoop: BoundaryLoop,
        private readonly rightWallBoundaryLoop: BoundaryLoop,
        public readonly leftWingFacadeOrientation: CardinalDirection,
        public readonly rightWingFacadeOrientation: CardinalDirection,
    ) {
        const claddings = cornerCladdingsFactory.createFromSource(panelSource, wallCorner, modelOffset);
        this.cornerCladdings = claddings.cornerCladdingCells;
        this.leftWingCladdings = claddings.leftWingCladdingCells;
        this.rightWingCladdings = claddings.rightWingCladdingCells;
        this.setCladdingCellsCustomMaterial(!!panelSource.customPanelTypeId);

        this.wingPlanesGeometry = cornerCladdingsFactory.createWingPlanesGeometry(
            panelSource,
            wallCorner,
            modelOffset,
            wingBoundaryPlaneMaterial,
        );
        this._elementName = panelSource.elementName;
        this._installationSequence = panelSource.installationSequence;
        this._depth = panelSource.depth;
        this.origin = new THREE.Vector3().copy(wallCorner.origin)
            .add(new THREE.Vector3()
                .copy(modelOffset)
                .negate())
            .add(new THREE.Vector3(0, 0, wallCorner.heights[this.heightIndex] + panelSource.offset));

        for (const claddingCell of this.cornerCladdings) {
            claddingCell.assignId(this.panelDbId);
        }
    }

    get id(): string {
        return this.panelSource.id;
    }

    get elementType(): FacadeElementType.StandardCorner {
        return FacadeElementType.StandardCorner;
    }

    get installationSequence(): InstallationSequence {
        return this._installationSequence;
    }

    get depth(): Depth {
        return this._depth;
    }

    get panelTypeId(): string {
        return this.panelSource.panelTypeId;
    }

    get customPanelTypeId(): string | null {
        return this.panelSource.customPanelTypeId;
    }

    get wallCornerId(): string {
        return this.panelSource.wallCornerId;
    }

    get userUniqueId(): number {
        return this.panelSource.userUniqueId;
    }

    get elementName(): string {
        return this._elementName;
    }

    get height(): number {
        return this.panelSource.height;
    }

    get leftWing(): number {
        return this.panelSource.leftWing;
    }

    get rightWing(): number {
        return this.panelSource.rightWing;
    }

    get leftWallFaceId(): string {
        return this.wallCorner.left.wallFaceId;
    }

    get rightWallFaceId(): string {
        return this.wallCorner.right.wallFaceId;
    }

    get heightIndex(): number {
        return this.panelSource.heightIndex;
    }

    get offset(): number {
        return this.panelSource.offset;
    }

    get isReversed(): boolean {
        return this.angle > 180;
    }

    get angle(): number {
        return this.wallCorner.angle;
    }

    get thicknessLabel(): string {
        const index = findPredefinedThicknessIndex(this.panelSource.thickness);

        const predefinedThickness = predefinedPanelThicknesses[index];

        return predefinedThickness.name;
    }

    get style(): CladdingCellStyle {
        return this.cornerCladdings[0].style;
    }

    get isParapetPanel(): boolean {
        return this.panelSource.isParapetPanel;
    }

    public readonly origin: THREE.Vector3;

    readonly cornerCladdings: WallCornerCladdingCell[];

    readonly leftWingCladdings: WallPanelCladdingCell[];

    readonly rightWingCladdings: WallPanelCladdingCell[];

    createGeometry(): ExtrusionGeometry[] {
        return [
            ...this.cornerCladdings.flatMap(x => x.createGeometry()),
            ...this.leftWingCladdings.map(x => x.createGeometry()),
            ...this.rightWingCladdings.map(x => x.createGeometry()),
            ...this.wingPlanesGeometry,
        ];
    }

    update({ elementName, installationSequence }: PanelParameters) {
        this._elementName = elementName;
        this._installationSequence = installationSequence;
    }

    getSignedDistanceToLeftWing(point: THREE.Vector3) {
        return getSingedDistanceToXYPlane(point, this.leftWingHooksCoordinateSystem);
    }

    getSignedDistanceToRightWing(point: THREE.Vector3) {
        return getSingedDistanceToXYPlane(point, this.rightWingHooksCoordinateSystem);
    }

    getLeftWingWallCoordinateSystem(): THREE.Matrix4 {
        return this.leftWallBoundaryLoop.transform;
    }

    getRightWingWallCoordinateSystem(): THREE.Matrix4 {
        return this.rightWallBoundaryLoop.transform;
    }

    setPanelType(newPanelType: IPanelType, isRegularType: boolean) {
        if (isRegularType) {
            this.panelSource.panelTypeId = newPanelType.id;
        } else {
            this.panelSource.customPanelTypeId = newPanelType.id;
        }

        this.setCladdingCellsCustomMaterial(!!this.panelSource.customPanelTypeId || newPanelType.withoutCladdings);
    }

    setPanelWindowDesignParameters() {
        // TODO: index: number, parameters: IPanelWindowDesignParameters
        // when corner panels will have windows
        console.error("Corner panels do not have windows now.");
    }

    setLouvreCutouts() {
        // TODO: index: number, cutouts: IPanelLouvreCutouts
        // when corner panels will have louvres
        console.error("Corner panels do not have louvres now.");
    }

    setCustomPanelType(customPanelTypeId: string | null) {
        this.panelSource.customPanelTypeId = customPanelTypeId;

        this.setCladdingCellsCustomMaterial(!!customPanelTypeId);
    }

    computeLeftWingPanelTypeTitleMatrix(titleSize: THREE.Vector3): THREE.Matrix4 {
        const wingLength = this.isReversed ? this.rightWing : this.leftWing;

        return this.computeWingPanelTypeTitleMatrix(titleSize, this.leftWingHooksCoordinateSystem, wingLength);
    }

    computeRightWingPanelTypeTitleMatrix(titleSize: THREE.Vector3): THREE.Matrix4 {
        const wingLength = this.isReversed ? this.leftWing : this.rightWing;

        return this.computeWingPanelTypeTitleMatrix(titleSize, this.rightWingHooksCoordinateSystem, wingLength);
    }

    getWorldOrigin(targetVector?: THREE.Vector3): THREE.Vector3 {
        targetVector = targetVector || new THREE.Vector3();

        return targetVector.copy(this.origin);
    }

    private setCladdingCellsCustomMaterial(isCustomMaterial: boolean) {
        if (this.cornerCladdings[0]?.isCustomMaterial === isCustomMaterial) {
            return;
        }

        for (const cladding of this.cornerCladdings) {
            cladding.isCustomMaterial = isCustomMaterial;
        }

        for (const cladding of this.leftWingCladdings) {
            cladding.isCustomMaterial = isCustomMaterial;
        }

        for (const cladding of this.rightWingCladdings) {
            cladding.isCustomMaterial = isCustomMaterial;
        }
    }

    private computeWingPanelTypeTitleMatrix(
        titleSize: THREE.Vector3,
        wingMatrix: THREE.Matrix4,
        wingLength: number,
    ): THREE.Matrix4 {
        const position = new THREE.Vector3(
            0.5 * (wingLength - titleSize.x),
            0.5 * (this.height - titleSize.y),
            editorCladdingGeometryThickness + zFightingFixDistance,
        ).applyMatrix4(wingMatrix);

        return new THREE.Matrix4().copy(wingMatrix).setPosition(position);
    }
}

const tempOrigin = new THREE.Vector3();
const tempNormal = new THREE.Vector3();
const tempRadiusVector = new THREE.Vector3();

const getSingedDistanceToXYPlane = (point: THREE.Vector3, coordinateSystem: THREE.Matrix4) => {
    tempOrigin.setFromMatrixPosition(coordinateSystem);
    tempNormal.setFromMatrixColumn(2, coordinateSystem);

    tempRadiusVector.subVectors(point, tempOrigin);

    return tempNormal.dot(tempRadiusVector);
};
